import { useCallback, useMemo, type ReactElement } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import type { FieldError } from 'react-hook-form'
import { memberCheckTypeToReadableMap } from '@domainConstants'
import { makeCrossSectionLabel } from '@editorUtils'
import { filter, find, get, maxBy } from 'lodash-es'
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  TextField,
  Tooltip,
  useTheme,
} from '@mui/material'
import { TruncatingTooltip } from '@ui2/display'
import { MaterialSymbol } from '@ui2/icons'
import { useAppStore } from '@stores'
import { useResultsStore } from '@editorStores'
import {
  FC90K_CONVERSION_FACTOR,
  RESULTS2_DETAIL_ROW_HIGHLIGHT_BG,
  supportsGridLayoutSx,
} from '../../constants'
import type { DesignFormRootValues } from '../../types'
import {
  assignNearestUniqueSupportCompressionChecks,
  fmt,
  fmtUtilization,
  formatCmFromMeters,
  getAllowedOverutilizationPercentForCheck,
  getMemberCheckSettingSupportsContextFromIndex,
  type MemberCheckSettingSupportsContextIndex,
  isSelectedSupportHighlightMatch,
  matchesRelativePosition,
  setSelectedSupportHighlight,
  utilizationCellSx,
} from '../../utils'
import {
  InlineNumberField,
  SupportInlineCmField,
  SupportLineLikeQsFieldsForm,
  SupportLineLikeQsFieldsReadOnly,
} from './components/InlineFields'
import { inlineFieldSx } from './utils'

/** Second line under Bauteil: assembly (IW/AW + name where applicable), else QS. */
const assemblySubtitle = (assembly: Assembly): string => {
  const typePrefix =
    assembly.usage_type === 'Inner' ? 'IW' : assembly.usage_type === 'Outer' ? 'AW' : undefined
  const name = assembly.name?.trim()
  const primary = [typePrefix, name].filter(Boolean).join(' · ')
  if (primary) return primary
  const shortName = assembly.short_name?.trim()
  return shortName || '—'
}

/** Source labels for f_c_90_k — keep in sync with CSAndAPPopupV2 FC90KField. */
function formatFc90kSourceLabel(
  source: SupportConfig['f_c_90_k_source'],
  length: 'short' | 'long',
): string | null {
  if (source === 'cross_section') return length === 'short' ? 'Bauteil' : 'Bauteil'
  if (source === 'assembly') return length === 'short' ? 'Aufbau' : 'Aufbau der Wand'
  if (source === 'default') return length === 'short' ? 'Standard' : 'Standard (C24 - 2.5)'
  return null
}

const LINE_LIKE_SUPPORT_QS_EDIT_TYPES = new Set<ElementTypes>([
  'beams',
  'columns',
  'purlins',
  'rips',
  'lintels',
])

const isLineLikeSupportTarget = (elementType: ElementTypes | undefined): boolean =>
  elementType !== undefined && LINE_LIKE_SUPPORT_QS_EDIT_TYPES.has(elementType)

const findSupportConfigForChecks = (
  supportConfigsForChecks: SupportConfigForChecks[],
  relativePosition: string,
  supportConfigIndex: number,
): SupportConfigForChecks | undefined =>
  supportConfigsForChecks.find(({ relative_position }) =>
    matchesRelativePosition(relative_position, relativePosition),
  ) ?? supportConfigsForChecks[supportConfigIndex]

const supportGeometryCheckboxSx = {
  m: 0,
  '& .MuiFormControlLabel-label': {
    fontSize: 10,
    color: 'text.secondary',
  },
}

const supportConfigRowErrorMessage = (
  supportConfigsErrors: Array<FieldError | undefined> | undefined,
  supportConfigIndex: number,
): string | undefined => {
  if (!Array.isArray(supportConfigsErrors)) return undefined
  const e = supportConfigsErrors[supportConfigIndex]
  return e && typeof e === 'object' && 'message' in e && e.message ? String(e.message) : undefined
}

export type SupportGridRowProps = {
  supportConfig: SupportConfig
  elementIndex: number
  rowIndex: number
  onSelectSupportTarget: (supportGuid: string) => void
  disableForm?: boolean

  rootElementGuid: string
  rootElementType: ElementTypes
  supportsContextIndex?: MemberCheckSettingSupportsContextIndex

  // Whole-model lookups resolved once by the parent SupportsGrid and shared
  // across rows, rather than rebuilt per row on every element selection.
  getElementLabel: (element: string) => string
  elementTypesByGuid: Record<string, ElementTypes | undefined>
  elementIdToAssembly: Record<string, Assembly | undefined>
  positionGuidToWallGuid: Record<string, string>
}

export const SupportGridRow = ({
  supportConfig,
  elementIndex,
  onSelectSupportTarget,
  rowIndex,
  disableForm = false,
  rootElementGuid,
  rootElementType,
  supportsContextIndex,
  getElementLabel,
  elementTypesByGuid,
  elementIdToAssembly,
  positionGuidToWallGuid,
}: SupportGridRowProps): ReactElement | null => {
  const theme = useTheme()
  const designForces = useResultsStore(state => state.designForces)
  const highlightedCheck = useResultsStore(state => state.highlightedCheck)
  const setHighlightedCheck = useResultsStore(state => state.setHighlightedCheck)

  const memberSettingsPath = `elements.0.memberCheckSettingsSelectedElement`

  const {
    setValue,
    formState: { errors },
  } = useFormContext<DesignFormRootValues>()

  const supportConfigsErrors = get(errors, `${memberSettingsPath}.support_configs`) as
    | Array<FieldError | undefined>
    | undefined

  const supportGuid = useWatch<DesignFormRootValues>({
    name: `elements.${elementIndex}.elementGuid`,
  }) as string | undefined

  const connectedSliceIdx = elementIndex

  const resolvedSupportCs = useWatch<DesignFormRootValues>({
    name: `elements.${connectedSliceIdx}.crossSection`,
  }) as CrossSectionTimberOrCLTOrSteel | null | undefined

  const supportingChecks = useWatch<DesignFormRootValues>({
    name: `elements.${connectedSliceIdx}.checksSelectedElement`,
  }) as CombinedPositionCheck[] | undefined

  const rootChecks = useWatch<DesignFormRootValues>({
    name: 'elements.0.checksSelectedElement',
  }) as CombinedPositionCheck[] | undefined

  const checkSpecificSettings = useWatch<DesignFormRootValues>({
    name: `elements.${connectedSliceIdx}.memberCheckSettingsSelectedElement.check_specific_settings`,
  }) as MemberCheckSpecificSetting[] | undefined

  const supportConfigsForChecks = useWatch<DesignFormRootValues>({
    name: `elements.0.supportConfigsForChecks`,
  }) as SupportConfigForChecks[] | undefined

  const supportConfigToCheckRelPositionTolerance = useAppStore(
    state => state.supportConfigToCheckRelPositionTolerance,
  )

  const maxNonSupportCompressionCheck = useMemo(() => {
    const nonSupportCompressionChecks = filter(
      supportingChecks,
      check =>
        check.check_type !== 'SupportCompression' && check.check_type !== 'SteelSupportCompression',
    )
    return maxBy(nonSupportCompressionChecks, 'max_utilization')
  }, [supportingChecks])

  const supportCompressionCheck = useMemo(() => {
    const supportCompressionChecks = filter(
      rootChecks,
      check =>
        check.check_type === 'SupportCompression' || check.check_type === 'SteelSupportCompression',
    ) as Array<SupportCompressionStructuralCheck | SteelCompressionCheck>

    return assignNearestUniqueSupportCompressionChecks(
      supportCompressionChecks,
      [supportConfig.relative_position],
      supportConfigToCheckRelPositionTolerance,
    )[0]
  }, [rootChecks, supportConfig.relative_position, supportConfigToCheckRelPositionTolerance])

  const hasTarget = supportGuid != null

  const assembly = useMemo(() => {
    if (supportGuid == null) return undefined
    return (
      elementIdToAssembly[supportGuid] ??
      (positionGuidToWallGuid[supportGuid]
        ? elementIdToAssembly[positionGuidToWallGuid[supportGuid]]
        : undefined)
    )
  }, [elementIdToAssembly, positionGuidToWallGuid, supportGuid])

  const designForce = useMemo(() => {
    if (supportGuid == null) return undefined
    const designForceList = find(designForces, ['element_guid', rootElementGuid])

    return designForceList?.design_support_forces.find(
      force => force.relative_position === supportConfig.relative_position,
    )
  }, [designForces, rootElementGuid, supportConfig.relative_position, supportGuid])

  const materialLine = useMemo(() => {
    if (resolvedSupportCs != null) {
      return makeCrossSectionLabel(resolvedSupportCs) ?? '—'
    }
    if (assembly) return assemblySubtitle(assembly)
    return '—'
  }, [resolvedSupportCs, assembly])

  const defaultsForChecks = useMemo(
    () =>
      supportConfigsForChecks &&
      (findSupportConfigForChecks(
        supportConfigsForChecks,
        supportConfig.relative_position,
        rowIndex,
      ) as (SupportConfigForChecks & Pick<SupportConfig, 'f_c_90_k_source'>) | undefined),
    [supportConfigsForChecks, supportConfig.relative_position, rowIndex],
  )

  const defaultFc90kSource = defaultsForChecks?.f_c_90_k_source
  const fc90kSourceLabel = useMemo(
    () => formatFc90kSourceLabel(defaultFc90kSource, 'short'),
    [defaultFc90kSource],
  )
  const fc90kSourceLabelLong = useMemo(
    () => formatFc90kSourceLabel(defaultFc90kSource, 'long'),
    [defaultFc90kSource],
  )

  const {
    isAreaExplicitlyGiven,
    isFc90kExplicitlyGiven,
    isOverhangExplicitlyGiven,
    shouldHideUtilization,
  } = useMemo(() => {
    const { area, overhang, f_c_90_k: fc90k } = supportConfig
    const areaGiven =
      area !== null && area !== undefined && area.length !== null && area.width !== null
    const fc90kGiven = fc90k !== null && fc90k !== undefined
    const overhangGiven = overhang !== null
    return {
      isAreaExplicitlyGiven: areaGiven,
      isFc90kExplicitlyGiven: fc90kGiven,
      isOverhangExplicitlyGiven: overhangGiven,
      shouldHideUtilization: !areaGiven && !overhangGiven,
    }
  }, [supportConfig])
  // Tolerances shown here belong to the supporting element, so they are
  // validated against that element's design context.
  const supportSettingValidationContext = useMemo(
    () => ({
      crossSection: resolvedSupportCs,
      supports: getMemberCheckSettingSupportsContextFromIndex(supportGuid, supportsContextIndex),
    }),
    [resolvedSupportCs, supportGuid, supportsContextIndex],
  )

  const utilizationSx = useMemo(() => {
    if (shouldHideUtilization) return null
    return utilizationCellSx(
      maxNonSupportCompressionCheck?.max_utilization ?? 0,
      theme,
      getAllowedOverutilizationPercentForCheck(
        maxNonSupportCompressionCheck,
        checkSpecificSettings,
        supportSettingValidationContext,
      ),
    )
  }, [
    shouldHideUtilization,
    maxNonSupportCompressionCheck,
    theme,
    checkSpecificSettings,
    supportSettingValidationContext,
  ])

  const supportCompressionUtilizationSx = useMemo(() => {
    if (shouldHideUtilization) return null
    return utilizationCellSx(supportCompressionCheck?.max_utilization ?? 0, theme)
  }, [shouldHideUtilization, supportCompressionCheck, theme])

  const rowErrorMessage = useMemo(
    () => supportConfigRowErrorMessage(supportConfigsErrors, rowIndex),
    [supportConfigsErrors, rowIndex],
  )

  const isRowHighlighted = useMemo(
    () =>
      isSelectedSupportHighlightMatch(
        rootElementGuid,
        highlightedCheck,
        supportConfig.relative_position,
      ),
    [rootElementGuid, highlightedCheck, supportConfig.relative_position],
  )

  const handleRowClick = useCallback(() => {
    setSelectedSupportHighlight(
      rootElementGuid,
      supportConfig.relative_position,
      setHighlightedCheck,
    )
  }, [rootElementGuid, setHighlightedCheck, supportConfig.relative_position])

  const { supportConfigPath, areaPath, overhangPath } = useMemo(() => {
    const path =
      `elements.0.memberCheckSettingsSelectedElement.support_configs.${rowIndex}` as const
    return {
      supportConfigPath: path,
      areaPath: `${path}.area` as const,
      overhangPath: `${path}.overhang` as const,
    }
  }, [rowIndex])

  // This is needed not to show the second support of rips and columns
  if ((rootElementType === 'rips' || rootElementType === 'columns') && connectedSliceIdx < 0)
    return null

  return (
    <Box sx={{ borderTop: `1px solid ${theme.palette.divider}` }}>
      <Box
        onClick={handleRowClick}
        sx={{
          ...supportsGridLayoutSx,
          py: 0.75,
          fontSize: 12,
          fontFamily: 'Roboto',
          cursor: 'pointer',
          backgroundColor: isRowHighlighted ? RESULTS2_DETAIL_ROW_HIGHLIGHT_BG : 'transparent',
        }}
      >
        <Box sx={{ textAlign: 'center', color: 'text.primary' }}>{rowIndex + 1}</Box>
        <Box sx={{ minWidth: 0 }}>
          {hasTarget && supportGuid != null ? (
            <Button
              type="button"
              variant="text"
              onClick={() => onSelectSupportTarget(supportGuid)}
              sx={{
                p: 0,
                minWidth: 0,
                justifyContent: 'flex-start',
                textAlign: 'left',
                textTransform: 'none',
                font: 'inherit',
                fontFamily: 'inherit',
                fontSize: 'inherit',
                lineHeight: 'inherit',
                color: theme.palette.primary.main,
                textDecoration: 'underline',
                display: 'block',
                '&:hover': {
                  backgroundColor: 'transparent',
                  textDecoration: 'underline',
                },
              }}
            >
              {getElementLabel(supportGuid) || supportGuid}
            </Button>
          ) : (
            <Box component="span" sx={{ color: 'text.primary' }}>
              {`Auflager ${rowIndex + 1}`}
            </Box>
          )}
          <Box sx={{ fontSize: 11, color: 'text.secondary', mt: 0.25 }}>
            <TruncatingTooltip text={materialLine} maxLength={13} />
          </Box>
          <Box>
            <Box component="span" mr={0.5}>
              η
            </Box>
            {shouldHideUtilization || !maxNonSupportCompressionCheck ? (
              <Box
                sx={{
                  color: utilizationSx?.color ?? 'text.secondary',
                  background: utilizationSx?.background ?? 'transparent',
                  fontWeight: utilizationSx?.fontWeight,
                  borderRadius: 0.5,
                  px: utilizationSx ? 0.375 : 0,
                  fontSize: 11,
                }}
              >
                —
              </Box>
            ) : (
              <Tooltip
                title={
                  memberCheckTypeToReadableMap[maxNonSupportCompressionCheck.check_type] ??
                  maxNonSupportCompressionCheck.check_type
                }
                arrow
                disableInteractive
                placement="right"
              >
                <Box
                  component="span"
                  sx={{
                    color: utilizationSx?.color ?? 'text.secondary',
                    background: utilizationSx?.background ?? 'transparent',
                    fontWeight: utilizationSx?.fontWeight,
                    borderRadius: 0.5,
                    px: utilizationSx ? 0.375 : 0,
                    fontSize: 11,
                  }}
                >
                  {fmtUtilization(maxNonSupportCompressionCheck.max_utilization)}
                </Box>
              </Tooltip>
            )}
          </Box>
        </Box>
        <Box sx={{ color: 'text.primary' }}>
          {designForce ? fmt(designForce.design_force / 1000) : '—'}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          {hasTarget &&
          supportGuid != null &&
          resolvedSupportCs != null &&
          isLineLikeSupportTarget(elementTypesByGuid[supportGuid]) ? (
            connectedSliceIdx >= 0 ? (
              <SupportLineLikeQsFieldsForm
                crossSectionPrefix={`elements.${connectedSliceIdx}.crossSection`}
                disableForm={disableForm}
              />
            ) : (
              <SupportLineLikeQsFieldsReadOnly crossSection={resolvedSupportCs} />
            )
          ) : (
            <Box component="span" sx={{ fontSize: 11, color: 'text.secondary' }}>
              —
            </Box>
          )}
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 0.25,
            lineHeight: 1.2,
          }}
        >
          {shouldHideUtilization || !supportCompressionCheck ? (
            <Box
              sx={{
                color: supportCompressionUtilizationSx?.color ?? 'text.primary',
                background: supportCompressionUtilizationSx?.background ?? 'transparent',
                fontWeight: supportCompressionUtilizationSx?.fontWeight,
                borderRadius: 0.5,
                px: supportCompressionUtilizationSx ? 0.375 : 0,
              }}
            >
              —
            </Box>
          ) : (
            <Tooltip
              title={
                memberCheckTypeToReadableMap[supportCompressionCheck.check_type] ??
                supportCompressionCheck.check_type
              }
              arrow
              disableInteractive
              placement="right"
            >
              <Box
                component="span"
                sx={{
                  color: supportCompressionUtilizationSx?.color ?? 'text.primary',
                  background: supportCompressionUtilizationSx?.background ?? 'transparent',
                  fontWeight: supportCompressionUtilizationSx?.fontWeight,
                  borderRadius: 0.5,
                  px: supportCompressionUtilizationSx ? 0.375 : 0,
                }}
              >
                {fmtUtilization(supportCompressionCheck.max_utilization)}
              </Box>
            </Tooltip>
          )}
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, width: 77 }}>
          <SupportInlineCmField
            disableForm={disableForm || !isAreaExplicitlyGiven}
            name={`${areaPath}.length`}
            label={
              <Box component="span">
                l<Box component="sub">A</Box>
              </Box>
            }
            placeholder={
              defaultsForChecks?.area?.length != null
                ? formatCmFromMeters(defaultsForChecks.area.length)
                : undefined
            }
          />
          <SupportInlineCmField
            disableForm={disableForm || !isAreaExplicitlyGiven}
            name={`${areaPath}.width`}
            label={
              <Box component="span">
                b<Box component="sub">A</Box>
              </Box>
            }
            placeholder={
              defaultsForChecks?.area?.width != null
                ? formatCmFromMeters(defaultsForChecks.area.width)
                : undefined
            }
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={isAreaExplicitlyGiven}
                disabled={disableForm}
                onChange={(_, checked) => {
                  setValue(
                    areaPath,
                    checked
                      ? defaultsForChecks?.area ?? {
                          length: 0.01,
                          width: 0.01,
                        }
                      : null,
                    { shouldDirty: true, shouldTouch: true },
                  )
                }}
                size="small"
                sx={{ p: 0 }}
              />
            }
            label="angeben"
            sx={supportGeometryCheckboxSx}
          />
        </Box>
        <Box gap={0.5}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              rowGap: 0.35,
              columnGap: 0.35,
              minWidth: 0,
            }}
          >
            <SupportInlineCmField
              disableForm={disableForm || !isOverhangExplicitlyGiven}
              name={`${overhangPath}.left`}
              label="l"
              placeholder={
                defaultsForChecks?.overhang?.left != null
                  ? formatCmFromMeters(defaultsForChecks.overhang.left)
                  : '0'
              }
            />
            <SupportInlineCmField
              disableForm={disableForm || !isOverhangExplicitlyGiven}
              name={`${overhangPath}.right`}
              label="r"
              placeholder={
                defaultsForChecks?.overhang?.right != null
                  ? formatCmFromMeters(defaultsForChecks.overhang.right)
                  : '0'
              }
            />
            <SupportInlineCmField
              disableForm={disableForm || !isOverhangExplicitlyGiven}
              name={`${overhangPath}.front`}
              label="v"
              placeholder={
                defaultsForChecks?.overhang?.front != null
                  ? formatCmFromMeters(defaultsForChecks.overhang.front)
                  : '0'
              }
            />
            <SupportInlineCmField
              disableForm={disableForm || !isOverhangExplicitlyGiven}
              name={`${overhangPath}.back`}
              label="h"
              placeholder={
                defaultsForChecks?.overhang?.back != null
                  ? formatCmFromMeters(defaultsForChecks.overhang.back)
                  : '0'
              }
            />
          </Box>
          <Box sx={{ gridColumn: '1 / -1' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isOverhangExplicitlyGiven}
                  disabled={disableForm}
                  onChange={(_, checked) => {
                    setValue(
                      overhangPath,
                      checked
                        ? defaultsForChecks?.overhang ?? {
                            left: 0,
                            right: 0,
                            front: 0,
                            back: 0,
                          }
                        : null,
                      { shouldDirty: true, shouldTouch: true },
                    )
                  }}
                  size="small"
                  sx={{ p: 0 }}
                />
              }
              label="angeben"
              sx={supportGeometryCheckboxSx}
            />
          </Box>
        </Box>
        <Controller
          name={`${supportConfigPath}.k_c_90`}
          render={({ field }) => (
            <TextField
              select
              size="small"
              variant="standard"
              value={field.value ?? 1}
              onChange={field.onChange}
              disabled={disableForm}
              fullWidth
              InputProps={{ sx: { fontSize: 12 } }}
              sx={{
                ...inlineFieldSx,
                // Shrink the dropdown arrow and the space it reserves so the value fits the narrow
                // k_c,90 column.
                '& .MuiSelect-icon': { fontSize: 16, right: 0 },
                '& .MuiSelect-select.MuiSelect-select': { pr: '14px' },
              }}
            >
              {[1.0, 1.25, 1.4, 1.5, 1.75, 1.9].map(value => (
                <MenuItem key={value} value={value} dense>
                  {fmt(value)}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
          <Box sx={{ width: 55 }}>
            <InlineNumberField
              name={`${supportConfigPath}.f_c_90_k`}
              conversionFactor={FC90K_CONVERSION_FACTOR}
              disableForm={disableForm || !isFc90kExplicitlyGiven}
              placeholder={
                defaultsForChecks?.f_c_90_k != null
                  ? fmt(Number(defaultsForChecks.f_c_90_k) / FC90K_CONVERSION_FACTOR)
                  : undefined
              }
            />
          </Box>
          {!isFc90kExplicitlyGiven && (
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                fontSize: 10,
                color: 'rgba(0,0,0,0.38)',
                pl: 0.25,
              }}
            >
              {fc90kSourceLabel}
              {fc90kSourceLabelLong && (
                <Tooltip
                  title={`Wert wird von '${fc90kSourceLabelLong}' bezogen`}
                  arrow
                  disableInteractive
                >
                  <Box component="span" sx={{ display: 'inline-flex' }}>
                    <MaterialSymbol icon="info" size={12} />
                  </Box>
                </Tooltip>
              )}
            </Box>
          )}

          <FormControlLabel
            control={
              <Checkbox
                checked={isFc90kExplicitlyGiven}
                disabled={disableForm}
                onChange={(_, checked) => {
                  setValue(
                    `${supportConfigPath}.f_c_90_k`,
                    checked ? defaultsForChecks?.f_c_90_k ?? 2.5 * FC90K_CONVERSION_FACTOR : null,
                    { shouldDirty: true, shouldTouch: true },
                  )
                }}
                size="small"
                sx={{ p: 0 }}
              />
            }
            label="angeben"
            sx={supportGeometryCheckboxSx}
          />
        </Box>
      </Box>
      {rowErrorMessage ? (
        <Box
          sx={{
            px: 1.25,
            pb: 0.75,
            fontSize: 11,
            lineHeight: 1.35,
            color: 'error.main',
            fontFamily: 'Roboto',
          }}
        >
          {rowErrorMessage}
        </Box>
      ) : null}
    </Box>
  )
}
